/**
 * Deterministic 2D value noise + fractal Brownian motion (fBm).
 *
 * Coordinate-hashed (not stream-based), so sampling order never matters — the
 * value at (x, y) depends only on the seed and the coordinate. Used by the map
 * generator to derive height / moisture / temperature fields.
 */

/** Hash two integer lattice coords + seed into a float in [0, 1). */
const hash2 = (ix: number, iy: number, seed: number): number => {
  let h = (seed ^ Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iy, 0x165667b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x297a2d39) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 0x100000000;
};

/** Quintic smoothstep (Perlin's fade) for C2-continuous interpolation. */
const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export class NoiseField {
  constructor(private readonly seed: number) {}

  /** Single-octave value noise, output in [0, 1). */
  value(x: number, y: number): number {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = fade(x - x0);
    const fy = fade(y - y0);

    const v00 = hash2(x0, y0, this.seed);
    const v10 = hash2(x0 + 1, y0, this.seed);
    const v01 = hash2(x0, y0 + 1, this.seed);
    const v11 = hash2(x0 + 1, y0 + 1, this.seed);

    return lerp(lerp(v00, v10, fx), lerp(v01, v11, fx), fy);
  }

  /**
   * Fractal Brownian motion — sum of `octaves` value-noise layers at increasing
   * frequency and decreasing amplitude. Output normalised to [0, 1).
   */
  fbm(x: number, y: number, octaves = 4, lacunarity = 2, gain = 0.5): number {
    let amplitude = 1;
    let frequency = 1;
    let sum = 0;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amplitude * this.value(x * frequency, y * frequency);
      norm += amplitude;
      amplitude *= gain;
      frequency *= lacunarity;
    }
    return norm > 0 ? sum / norm : 0;
  }
}
